import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ReactQuill from "react-quill";
import {
  Grid,
  IconButton,
  Typography,
  FormControl,
  TextField,
  FormHelperText,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { useCart } from "../../../hooks/shareContext";
import { axiosInstance } from "../../../utils/tokenSetCookie";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const CUSTOM_UPLOAD_BASE_URL = import.meta.env.VITE_CUSTOM_UPLOAD_BASE_URL;

interface BannerImages {
  bannerId: number;
  bannerUrl: string;
  bannerName: string;
}

interface FormErrors {
  fromEmailId?: string;
  toEmailId?: string;
  ccEmailId?: string;
  bccEmailId?: string;
  subject?: string;
  message?: string;
  appointmentId?: string;
  banner?: string;
  trackType?: string;
  languageId?: number;
  websiteId?: number;
  animationId?: string;
}

interface EmailData {
  emailData: EmailContent[];
  selectedIcon: (icon: string) => void;
}
interface EmailContent {
  subject: string;
  message: string;
  type: string;
}

const EmailShare: React.FC<EmailData> = ({ emailData, selectedIcon }) => {
  const { selectAnim, myVideosAnim } = useCart();
  const [toEmail, setToEmail] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [bccEmail, setBccEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [formatedemailtext, setFormattedEmailText] = useState("");
  const [appointmentId, setAppointmentId] = useState("");
  const [editorValue, setEditorValue] = useState(""); // To store corresponding message
  const [selectedImage, setSelectedImage] = useState(1); // Initially mentioned number is ID (Not a index)
  const [showCc, setShowCc] = useState(false); // To toggle CC field visibility
  const [showBcc, setShowBcc] = useState(false); // To toggle BCC field visibility
  const [isTomailValid, setIsTomailValid] = useState(true);
  const [isCCmailValid, setIsCCmailValid] = useState(true);
  const [isBCCmailValid, setIsBCCmailValid] = useState(true);
  const [_isSubjectValid, setIsSubjectValid] = useState(true);
  const [_isMessageValid, setIsMessageValid] = useState(true);
  const [isAppointmentIdValid, setIsAppointmentIdValid] = useState(true);

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [bannerImage, setBannerImage] = useState<BannerImages[]>([]);
  const { t } = useTranslation();

  // for message text toolbar design function
  const modules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ align: [] }],
      // ['link'],
    ],
  };
  console.log("bannerImage", bannerImage);
  

  // Get Banner Images
  useEffect(() => {
    const fetchBannerData = async () => {
      try {
        const response = await axiosInstance.get(API_BASE_URL + "banner/list");
        if (response.status >= 200 && response.status <= 300) {
          setBannerImage(response.data.data);
        } else {
          console.log("Error");
        }
      } catch (err) {
        console.log("Error", err);
      }
    };
    fetchBannerData(); // Passing the payload as an argument
  }, []);
  function stripHtml(html: string) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  }

  const handleEditorChange = (value: string) => {
    const plain_text = stripHtml(value);
    setFormattedEmailText(plain_text);
    setEditorValue(value);
    setIsMessageValid(value.trim().length > 0);
  };

  const options = emailData;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const handleEmailFieldChange = (
    field: "to" | "cc" | "bcc",
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const input = event.target.value;
    const emailArray = input
      .split(/[,\s]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    const allValid = emailArray.every((email) => emailRegex.test(email));

    // Update field value
    if (field === "to") setToEmail(input);
    else if (field === "cc") setCcEmail(input);
    else if (field === "bcc") setBccEmail(input);

    // Set validity flags (optional)
    if (field === "to") setIsTomailValid(allValid);
    else if (field === "cc") setIsCCmailValid(allValid);
    else if (field === "bcc") setIsBCCmailValid(allValid);

    // Set form errors
    setFormErrors((prev) => ({
      ...prev,
      [`${field}EmailId`]: allValid ? "" : "Invalid email address format",
    }));
  };
  const handleSubjectChange = (
    _event: React.SyntheticEvent<Element, Event>,
    newValue: string | null
  ) => {
    if (newValue) {

      // Replace input with the selected option
      setSubject(newValue);
      // Update the corresponding message
      const selectedOption = options.find(
        (option) => option.subject === newValue
      );
      setEditorValue(selectedOption?.message || "");
    }
  };

  const handleSubjectInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Allow the user to append text to the current value
    const newSubject = event.target.value;
    setSubject(newSubject);
    setIsSubjectValid(newSubject.trim().length > 0);
  };
  const handleAppointmentIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // setAppointmentId(event.target.value);
    const newAppointmentId = event.target.value;
    setAppointmentId(newAppointmentId);
    setIsAppointmentIdValid(newAppointmentId.trim().length > 0);
  };

  const handleImageSelect = (id: number) => {
    setSelectedImage(id);
  };


  const validateForm = () => {
    const errors: FormErrors = {};

    if (toEmail === "" || !isTomailValid) {
      errors.toEmailId = "Please enter a valid email address.";
    }
    if (!isCCmailValid) {
      errors.ccEmailId = "Please enter a valid CC email address.";
    }
    if (!isBCCmailValid) {
      errors.bccEmailId = "Please enter a valid BCC email address.";
    }
    if (!subject.trim()) {
      errors.subject = "Please enter a subject.";
    }
    if (!editorValue.trim()) {
      errors.message = "Please enter a message.";
    }
    if (!isAppointmentIdValid) {
      errors.appointmentId = "Please enter a work order number.";
    }
    if (!selectedImage) {
      errors.banner = "Please select an image.";
    }

    // Set the errors state
    setFormErrors(errors);

    // Return false if there are any errors, true otherwise
    return Object.keys(errors).length === 0;
  };

  // Function to reset all fields
  const resetForm = () => {
    setToEmail("");
    setCcEmail("");
    setBccEmail("");
    setSubject("");
    setAppointmentId("");
    setEditorValue("");
  };

  const handleSubmit = async (event: React.FormEvent) => {
    // Prevent the default form submission behavior
    event.preventDefault();
    if (!validateForm()) {
      return;
    }


    const emailData = {
      fromEmailId: "videos@clinicalvisuals.com",
      toEmailId: toEmail
        .split(/[,\s]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0)
        .join(","),
      CCEmailId: ccEmail
        .split(/[,\s]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0)
        .join(","),
      BCCEmailId: bccEmail
        .split(/[,\s]+/)
        .map((email) => email.trim())
        .filter((email) => email.length > 0)
        .join(","),
      subject: subject,
      message: formatedemailtext,
      appointmentId: appointmentId,
      banner: bannerImage.find((banner) => banner.bannerId === selectedImage)
        ?.bannerUrl,
      trackType: "share_email",
      languageId: 50,
      websiteId: 1,
      animationId: selectAnim.join(","),
      myVideos: myVideosAnim.join(","),
    };

    const animationShareEmail = async (emailData: any) => {
      try {
        const response = await axiosInstance.post(
          API_BASE_URL + "share/email-send",
          emailData
        );

        if (response.status >= 200 && response.status <= 300) {
          setTimeout(() => {
            toast.success(response.data.message, {
              position: "top-right",
              autoClose: 1000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            });
          }, 200);

          selectedIcon("");
          // Reset all fields after submission
          resetForm();
        } else {
        }
      } catch (error) {
        console.error("Failed to send email:", error);
        toast.error(t("failed_to_send_email_please_try_again."), {
          position: "top-right",
          autoClose: 3000,
        });
      }
    };
    animationShareEmail(emailData);


    // TODO: Implement API call to send email or other handling logic
  };

  return (
    <>
      {/* <ToastContainer /> */}
      <form className="EmailShare">
        <Grid container spacing={2}>
          {/* From Field */}
          <Grid item xs={12}>
            <TextField
              className="email_input to_email"
              label={t("from")}
              fullWidth
              variant="outlined"
              name="fromEmailId"
              value="videos@clinicalvisuals.com" // Assuming the email is pre-filled, replace with dynamic value
              sx={{
                fontWeight: "bold",
              }}
              disabled
            />
          </Grid>

          {/* To Field (as Textarea with CC/BCC buttons inside) */}
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth error={!isTomailValid}>
              <TextField
                className="email_input"
                label={t("to")}
                fullWidth
                variant="outlined"
                multiline
                required
                rows={1}
                error={!!formErrors.toEmailId}
                helperText={formErrors.toEmailId}
                onChange={(e) => handleEmailFieldChange("to", e)} // change event
                name="toEmailId"
                value={toEmail}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: "10px 14px",
                  },
                }}
                placeholder="Enter email addresses here"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {!showCc && (
                        <IconButton
                          onClick={() => setShowCc(!showCc)}
                          title="Toggle CC"
                          className="cc_bcc"
                        >
                          CC
                        </IconButton>
                      )}
                      {!showBcc && (
                        <IconButton
                          onClick={() => setShowBcc(!showBcc)}
                          title="Toggle BCC"
                          className="cc_bcc"
                        >
                          BCC
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </Grid>

          {/* CC Field */}
          {showCc && (
            <Grid item xs={12}>
              <FormControl fullWidth error={!isCCmailValid}>
                <TextField
                  className="email_input"
                  label="CC"
                  name="CCEmailId"
                  value={ccEmail}

                  fullWidth
                  variant="outlined"
                  error={!!formErrors.ccEmailId}
                  helperText={formErrors.ccEmailId}
                  onChange={(e) => handleEmailFieldChange("cc", e)} // change event
                />
                <IconButton
                  onClick={() => setShowCc(false)}
                  title="Close CC"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#777777",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </FormControl>
            </Grid>
          )}

          {/* BCC Field */}
          {showBcc && (
            <Grid item xs={12}>
              <FormControl fullWidth error={!isBCCmailValid}>
                <TextField
                  className="email_input"
                  label="BCC"
                  name="BCCEmailId"
                  value={bccEmail}

                  fullWidth
                  variant="outlined"
                  error={!!formErrors.bccEmailId}
                  helperText={formErrors.bccEmailId}
                  onChange={(e) => handleEmailFieldChange("bcc", e)} // change event
                />
                <IconButton
                  onClick={() => setShowBcc(false)}
                  title="Close BCC"
                  style={{
                    position: "absolute",
                    right: "10px",
                    color: "#777777",
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </FormControl>
            </Grid>
          )}

          {/* Subject Field */}
          <Grid item xs={12}>
            
           
            <Autocomplete
              freeSolo
              value={subject}
              onChange={handleSubjectChange} // Handles dropdown option selection
              inputValue={subject} // Reflects the current input value
              onInputChange={(event) => {
                if (event.type === "change") {
                  handleSubjectInputChange(
                    event as React.ChangeEvent<HTMLInputElement>
                  );
                }
              }}
              options={options.map((option) => option.subject)} // Provide options for the dropdown
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("subject")}
                  variant="outlined"
                  placeholder="Type or select a subject"
                  fullWidth
                  required
                  name="subject"
                  error={!!formErrors.subject}
                  helperText={formErrors.subject}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      // borderRadius: "10px",
                      padding: "7px 14px",
                    },
                  }}
                />
              )}
            />
          </Grid>

          {/* Message Field */}
          <Grid item xs={12}>
            <ReactQuill
              className="email_input"
              value={editorValue}
              onChange={handleEditorChange}
              theme="snow"
              placeholder={t("write_something")}
              style={
                formErrors.message
                  ? { border: "1px solid #d32f2f", fontSize: "16px" }
                  : {}
              }
              modules={modules}
            />
            <TextField
              className="email_input "
              label="message"
              name="message"
              value={editorValue}
              fullWidth
              variant="outlined"
              error={!!formErrors.message}
              helperText={formErrors.message}
              disabled
              sx={{ display: "none", fontSize: "16px" }} // Hides the TextField
            />
            {formErrors.message && (
              <FormHelperText sx={{ color: "#d32f2f" }}>
                {formErrors.message}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={12}>
            <TextField
              className="email_input"
              label={t("appointment_id")}
              fullWidth
              variant="outlined"
              value={appointmentId}
              onChange={handleAppointmentIdChange}
              error={!!formErrors.appointmentId}
              helperText={formErrors.appointmentId}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              {t("share_select_banner")}
            </Typography>
            <Grid container spacing={2} justifyContent="flex-start">
              {bannerImage.map((item) => (
                <Grid item key={item.bannerId}>
                  <img
                    src={CUSTOM_UPLOAD_BASE_URL + item.bannerUrl}
                    alt={item.bannerName}
                    style={{
                      width: "75px",
                      height: "75px",
                      cursor: "pointer",
                      opacity: selectedImage === item.bannerId ? "1" : 0.5,
                    }}
                    onClick={() => handleImageSelect(item.bannerId)}
                  />
                </Grid>
              ))}
            </Grid>
            <TextField
              name="trackType"
              value=""
              disabled
              sx={{ display: "none" }} // Hides the TextField
            />
          </Grid>

          {/* Submit Button */}
          <Grid item xs={12}>
            <IconButton
              type="submit"
              color="primary"
              onClick={handleSubmit}
              className="emailShareButton"
            >
              <Typography variant="button" sx={{ textTransform: "none" }}>
                {t("send")}
              </Typography>
            </IconButton>
            <IconButton color="primary" className="emailShareButton">
              <Typography variant="button" sx={{ textTransform: "none" }}>
                {t("cancel")}
              </Typography>
            </IconButton>
          </Grid>
        </Grid>
      </form>
    </>
  );
};

export default EmailShare;
